import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { MysterySymbols } from "@/components/MysterySymbols";
import { LOGO, IMG, PROGRAMS, EXPLORERS, VENUE } from "@/lib/brand";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DaytimePromo } from "@/components/DaytimePromo";

function Wave({ className = "", color = "#00C2CB" }) {
  return (
    <svg viewBox="0 0 1440 120" className={`w-full ${className}`} preserveAspectRatio="none">
      <path fill={color} d="M0,64 C240,120 480,0 720,48 C960,96 1200,32 1440,64 L1440,120 L0,120 Z" opacity="0.9" />
    </svg>
  );
}

export default function Home() {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/public/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  return (
    <div className="bg-navy min-h-screen text-white overflow-x-hidden">
      <Navbar />
      <MysterySymbols />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-16 grain">
        <div className="absolute inset-0">
          <img src={IMG.heroPortal} alt="Adventure Rock portal" className="w-full h-full object-cover" fetchpriority="high" decoding="async" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy/90 to-transparent" />
        </div>
        <div className="absolute top-24 right-8 opacity-20 hidden lg:block">
          <Icons.Compass className="w-72 h-72 text-turquoise animate-spin-slow" strokeWidth={0.5} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-2xl">
            <span className="inline-block text-turquoise font-bold tracking-[0.3em] text-sm mb-4">BERMUDA'S CHILDREN'S PLAY CAFÉ</span>
            <h1 className="font-display font-black text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              PLAY. EXPLORE. <br /><span className="text-gold">THE ADVENTURE</span><br /> BEGINS.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/80 max-w-lg">
              Adventure Rock Bermuda is a full children's play café and indoor playground — a mysterious island world where every child becomes an Explorer while parents relax with a coffee.
            </p>

            <span className="inline-block mt-6 bg-gold text-navy font-black rounded-full px-4 py-1.5 text-sm tracking-wide" data-testid="hero-open-badge">OPEN DAILY · 9:00 AM – 6:00 PM</span>
            <p className="mt-3 text-sm text-white/70" data-testid="hero-afterschool-note">Day passes, parties, events and camps — plus our <b className="text-turquoise">after-school program</b> is still available.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/daytime"><Button className="bg-gold hover:bg-gold/90 text-navy font-extrabold rounded-full text-base px-8 py-6 w-full sm:w-auto" data-testid="hero-register-button">BOOK A PLAY VISIT</Button></Link>
              <Link to="/events"><Button variant="outline" className="border-turquoise text-turquoise hover:bg-turquoise hover:text-navy rounded-full text-base px-8 py-6 w-full sm:w-auto bg-transparent" data-testid="hero-explore-button">SEE EVENTS</Button></Link>
              <Link to="/pricing"><Button variant="ghost" className="text-white hover:text-gold rounded-full text-base px-6 py-6 w-full sm:w-auto underline underline-offset-4" data-testid="hero-pricing-button">See Pricing</Button></Link>
            </div>
          </motion.div>
        </div>
        <div className="absolute -bottom-1 left-0 w-full"><Wave color="#1C2541" /></div>
      </section>

      {/* REGISTRATION */}
      <section className="bg-ocean py-14">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 text-gold font-bold tracking-widest text-xs"><Icons.PartyPopper className="w-4 h-4" /> THIS SATURDAY · OCTOBER 31</div>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl mt-2">Costume Party · 9 AM – 9 PM</h2>
          <p className="text-white/70 mt-3 max-w-2xl mx-auto">$45 per child — popcorn, cupcake & cookie decorating, cotton candy, karaoke and crafts. Booking required.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/events"><Button className="bg-turquoise hover:bg-turquoise/90 text-navy font-bold rounded-full px-8" data-testid="reg-period-cta">Book the Costume Party</Button></Link>
            <Link to="/register"><Button variant="outline" className="border-white/40 text-white hover:bg-white/10 rounded-full px-8 bg-transparent" data-testid="home-register-btn">Create a parent account</Button></Link>
          </div>
        </div>
      </section>

      <DaytimePromo />

      {/* PROGRAMS */}
      <section className="relative bg-navy py-20 grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-turquoise font-bold tracking-[0.25em] text-sm">DISCOVER</span>
            <h2 className="font-display font-black text-4xl sm:text-5xl mt-2">WHAT HAPPENS BEYOND THE ROCK?</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PROGRAMS.map((p, i) => {
              const Icon = Icons[p.icon.split("-").map((s, idx) => idx === 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s.charAt(0).toUpperCase() + s.slice(1)).join("")] || Icons.Compass;
              return (
                <motion.div key={p.key} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                  whileHover={{ y: -6 }} className="glass border border-white/10 rounded-2xl p-6 hover:border-turquoise/50 transition-colors" data-testid={`program-card-${p.key}`}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: p.color + "22" }}>
                    <Icon className="w-6 h-6" style={{ color: p.color }} />
                  </div>
                  <h3 className="font-display font-bold text-lg">{p.title}</h3>
                  <p className="text-white/60 text-sm mt-1">{p.desc}</p>
                </motion.div>
              );
            })}
          </div>
          <div className="text-center mt-10"><Link to="/program"><Button variant="outline" className="border-turquoise text-turquoise hover:bg-turquoise hover:text-navy rounded-full bg-transparent" data-testid="programs-more-btn">See the full program</Button></Link></div>
        </div>
      </section>

      {/* VENUE */}
      <section className="bg-abyss py-20 grain" data-testid="venue-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-gold font-bold tracking-[0.25em] text-sm">STEP INSIDE THE ROCK</span>
            <h2 className="font-display font-black text-4xl sm:text-5xl mt-2">A WHOLE WORLD UNDER ONE ROOF</h2>
            <p className="text-white/70 mt-3 max-w-2xl mx-auto">Adventure Rock is a giant indoor playground — bounce, create, game and refuel, all in one epic island base.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VENUE.map((v, i) => {
              const Icon = Icons[v.icon] || Icons.Sparkles;
              return (
                <motion.div key={v.key} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                  className="group relative rounded-2xl overflow-hidden border border-white/10 aspect-[3/4]" data-testid={`venue-${v.key}`}>
                  <img src={v.img} alt={v.title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/50 to-transparent" />
                  <div className="absolute bottom-0 p-5">
                    <Icon className="w-8 h-8 text-gold mb-2 drop-shadow" />
                    <h3 className="font-display font-bold text-lg text-white leading-tight">{v.title}</h3>
                    <p className="text-white/70 text-sm mt-1">{v.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ADVENTURE CREDIT */}
      <section className="relative py-20 bg-cover bg-center" style={{ backgroundImage: `linear-gradient(rgba(8,14,30,0.85),rgba(8,14,30,0.92)), url(${IMG.mapTexture})` }}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Icons.Ticket className="w-12 h-12 text-gold mx-auto mb-4" />
          <h2 className="font-display font-black text-3xl sm:text-4xl text-gold">PLANS CHANGE. YOUR ADVENTURE DOESN'T DISAPPEAR.</h2>
          <p className="text-white/80 mt-4 text-lg">Miss a booked day? We convert it into an <b className="text-turquoise">Adventure Credit</b> you can use on another open program date. Flexible, fair and stress-free.</p>
        </div>
      </section>

      {/* MEET THE EXPLORERS */}
      <section className="bg-ocean py-20 grain" data-testid="meet-explorers-section">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <span className="text-pink font-bold tracking-[0.25em] text-sm">CURIOUS KIDS · BRIGHTER TOMORROWS</span>
            <h2 className="font-display font-black text-4xl sm:text-5xl mt-2">MEET THE EXPLORERS</h2>
            <p className="text-white/70 mt-3 max-w-2xl mx-auto">Every child who joins Adventure Rock becomes an Explorer. Small explorers, big change.</p>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
            className="rounded-3xl overflow-hidden border-2 border-turquoise/30 shadow-2xl">
            <img src={IMG.mascots} alt="Adventure Rock Kids — the Explorers" loading="lazy" decoding="async" className="w-full object-cover" />
          </motion.div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mt-8">
            {EXPLORERS.map((e, i) => (
              <motion.div key={e.name} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="glass border border-white/10 rounded-2xl p-4 text-center" data-testid={`explorer-mascot-${e.name.toLowerCase()}`}>
                <div className="w-3 h-3 rounded-full mx-auto mb-2" style={{ backgroundColor: e.color }} />
                <p className="font-display font-bold text-white">{e.name}<span className="text-white/40 text-sm">, {e.age}</span></p>
                <p className="text-[10px] tracking-wide font-semibold uppercase" style={{ color: e.color }}>{e.trait}</p>
                <p className="text-white/50 text-xs mt-1 leading-snug">{e.motto}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GOLD MEMBERSHIP TEASER */}
      <section className="bg-navy py-20 grain">
        <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="text-gold font-bold tracking-[0.25em] text-sm">EXCLUSIVE</span>
            <h2 className="font-display font-black text-4xl mt-2">THE GOLD EXPLORER MEMBERSHIP</h2>
            <p className="text-white/70 mt-4">A collectible Adventure Rock Gold Card for your explorer — a unique Explorer ID, secure QR code for cashless snacks & check-in, and founding member status.</p>
            <Link to="/register"><Button className="mt-6 bg-gradient-to-r from-gold to-coral text-navy font-bold rounded-full px-8" data-testid="gold-teaser-btn">Become a Gold Explorer</Button></Link>
          </div>
          <motion.div whileHover={{ rotateY: 8, rotateX: -4 }} style={{ perspective: 1000 }}
            className="relative rounded-3xl overflow-hidden border border-gold/40 aspect-[1.6/1] bg-cover bg-center shadow-2xl" >
            <img src={IMG.goldCardBg} alt="Gold card" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 gold-shine" />
            <div className="relative p-6 h-full flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <img src={LOGO} className="w-12 h-12 object-contain" alt="logo" />
                <span className="font-display font-bold text-gold tracking-widest text-xs">GOLD EXPLORER</span>
              </div>
              <div>
                <p className="font-mono text-gold/80 text-xs">ARB-2026-••••</p>
                <p className="font-display font-black text-2xl text-white">EXPLORER</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
